import { Flex } from '@chakra-ui/react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import AffiliationAddressRow from './AffiliationAddressRow';
import { ButtonText, useToast } from '@morressier/alembic';
import { useCallback, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus } from '@fortawesome/pro-regular-svg-icons';
import { IAffiliationDetails } from '@publishing-engine/domain/models';
import { handleDuplicateCheck } from '../StepThree/signupHook';

const defaultAddress = {
  organizationName: '',
  streetAddress: '',
  city: '',
  stateOrRegion: '',
  postalCode: '',
  country: '',
  countryCode: '',
  ringgoldId: '',
  url: '',
  selectedAffiliation: null,
  searchValue: ''
};

const MultipleAffiliationForm = ({
  formConfig,
  additionalAffiliations
}: {
  formConfig: any;
  additionalAffiliations?: IAffiliationDetails[] | null | undefined;
}) => {
  const toast = useToast();
  const {
    control,
    trigger,
    clearErrors,
    setValue,
    getValues,
    formState: { errors }
  } = useFormContext();
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'additionalAffiliations'
  });

  const handleMultipleAffiliation = useCallback(async () => {
    const lastIdx = fields.length - 1;
    const { additionalAffiliations, affiliationDetails } = getValues();

    const selectedAffiliation = additionalAffiliations[lastIdx];

    if (selectedAffiliation && fields.length > 0) {
      const isDuplicateMatches = handleDuplicateCheck(
        selectedAffiliation,
        `additionalAffiliations.${lastIdx}`,
        additionalAffiliations,
        affiliationDetails
      );

      if (isDuplicateMatches.length) {
        const duplicatesLabel = isDuplicateMatches
          .map(m =>
            m.index === 'primary'
              ? 'Primary affiliation'
              : `Secondary affiliation(s) ${Number(m.index) + 1}`
          )
          .join(', ');

          toast({
            variant: 'error',
            title: 'Duplicate affiliation detected',
            description: `Secondary affiliation(s) ${lastIdx + 1} is identical to ${duplicatesLabel}. Please modify one of these entries to continue.`,
            isClosable: true,
          });

        return; // stop if any duplicate field
      } else clearErrors(`additionalAffiliations.${lastIdx}`);

    }

    const isValid = await trigger(`additionalAffiliations.${lastIdx}`);

    if (isValid) {
      const newIdx = fields.length;
      append({ ...defaultAddress });
      setValue(`additionalAffiliations.${newIdx}.organizationName`, '');
      clearErrors(`additionalAffiliations.${newIdx}`);
    }
  }, [fields, append, trigger, clearErrors, getValues, errors, setValue]);

  const handleRemove = useCallback(
    (idx: number) => {
      const remainingFields = fields.length - 1;
      remove(idx);

      // Clear errors only for remaining/updated fields after remove
      for (let i = 0; i < remainingFields; i++) {
        clearErrors(`additionalAffiliations.${i}`);
      }
    },
    [remove, clearErrors, fields.length]
  );

  useEffect(() => {
    if (additionalAffiliations) {
      const affiliationsWithSearchValue = additionalAffiliations.map(
        affiliation => ({
          ...affiliation,
          searchValue: affiliation.organizationName || ''
        })
      );
      setValue('additionalAffiliations', affiliationsWithSearchValue);
    } else {
      setValue('additionalAffiliations', [defaultAddress]);
    }
  }, [additionalAffiliations, setValue]);

  return (
    <Flex direction={'column'}>
      {fields.map((field, idx) => (
        <AffiliationAddressRow
          idx={idx}
          key={field.id}
          fieldsLength={fields.length}
          formConfig={formConfig}
          onRemove={handleRemove}
          defaultValue={
            (additionalAffiliations &&
              additionalAffiliations[idx]?.organizationName) ||
            ''
          }
        />
      ))}
      <Flex gap={3} justifyContent={{ base: 'stretch', md: 'flex-end' }}>
        <ButtonText
          type="button"
          onClick={handleMultipleAffiliation}
          iconLeft={<FontAwesomeIcon icon={faPlus} />}
          width={{ base: '100%', md: 'auto' }}
        >
          Add secondary affiliation(s)
        </ButtonText>
      </Flex>
    </Flex>
  );
};

export default MultipleAffiliationForm;
